import { DTO } from '../../utilities/Dto';

/**
 * One row of the api.catalog_content view: either a folder (group) or a
 * file slot (filetoken with the current revision joined in).
 *
 * The view UNIONs catalog_group + catalog_filetoken into a single
 * heterogeneous shape so the frontend can fetch the whole catalog tree in
 * one request and discriminate rows by `kind`.
 *
 * File-only columns are null for group rows; group-only columns are null for
 * file rows. Labels (nl/en/de/fr/es) come from public.docs_name (ru/tr are
 * deferred — they live in the docs.docs_name view on side tables).
 */
export interface CatalogContentRow {
  /** Stable composite id (`'group:'|'file:' + recnum`). */
  id: string;
  kind: 'group' | 'file';
  recnum: number;
  catalog_id: number;
  project_id: number | null;
  /** Parent group recnum. null for root groups. */
  parent_group_id: number | null;
  ordering: number;

  // Group-only
  prefix_required: string | null;

  // File-only
  filetype: string | null;
  prefix: string | null;
  duplicatenr: number | null;
  /** catalog_file.recnum — id used by /files/:id download endpoint. */
  file_recnum: number | null;
  file_extension: string | null;
  /** MIME joined from docs.mime_types via file_extension. */
  mime: string | null;
  date_in: Date | null;
  revision_nr: number | null;
  revision_message: string | null;
  checksum: string | null;
  size: number | null;

  // Labels (5 langs)
  nl: string | null;
  en: string | null;
  de: string | null;
  fr: string | null;
  es: string | null;
}

export type CatalogContentGroup = CatalogContentRow & {
  kind: 'group';
  file_recnum: null;
};
export type CatalogContentFile = CatalogContentRow & { kind: 'file' };

export function isCatalogContentGroup(
  r: CatalogContentRow,
): r is CatalogContentGroup {
  return r.kind === 'group';
}
export function isCatalogContentFile(
  r: CatalogContentRow,
): r is CatalogContentFile {
  return r.kind === 'file';
}

export function parseCatalogContentRowDto(
  r: DTO<CatalogContentRow>,
): CatalogContentRow {
  return {
    ...r,
    date_in: r.date_in ? new Date(r.date_in) : null,
  };
}

/**
 * Static list of MIME types known to the docs catalog. Sourced from
 * SELECT DISTINCT mime FROM docs.mime_types as of 2026-05. Used to populate
 * filter dropdowns in the frontend without an extra round-trip; the
 * canonical mapping (extension -> mime) is applied server-side in the
 * api.catalog_content view.
 */
export const DOCS_MIME_TYPES = [
  'application/acad',
  'application/msword',
  'application/pdf',
  'application/postscript',
  'application/rtf',
  'application/vnd.google-earth.kml+xml',
  'application/vnd.google-earth.kmz',
  'application/vnd.ms-access',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.addin.macroEnabled.12',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-excel.template.macroEnabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.ms-powerpoint.addin.macroEnabled.12',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
  'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
  'application/vnd.ms-powerpoint.template.macroEnabled.12',
  'application/vnd.ms-word.document.macroEnabled.12',
  'application/vnd.ms-word.template.macroEnabled.12',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.wap.wmlc',
  'application/zip',
  'audio/midi',
  'audio/mpeg',
  'audio/ogg',
  'audio/x-m4a',
  'audio/x-realaudio',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/vnd.wap.wbmp',
  'image/x-icon',
  'image/x-jng',
  'image/x-ms-bmp',
  'text/html',
  'text/plain',
  'video/3gpp',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/x-flv',
  'video/x-m4v',
  'video/x-mng',
  'video/x-ms-asf',
  'video/x-ms-wmv',
  'video/x-msvideo',
] as const;

export type DocsMimeType = (typeof DOCS_MIME_TYPES)[number];
